<template>
    <div>
        <card-header title="Payment Plan Taxes" icon="fa-file-invoice-dollar" />

        <card-toolbar>
                <button @click="exportCsv" :disabled="exporting">
                    <i class="fas fa-download"></i>
                    {{ exporting ? 'Exporting...' : 'Export CSV' }}
                </button>
        </card-toolbar>

        <card-body>
            <!-- Summary Section -->
            <container-list-item padded class="pt-3 pb-3" v-if="summary">
                <div class="summary-grid">
                    <div class="summary-stat">
                        <div class="stat-label">Total Tax</div>
                        <div class="stat-value">{{ formatCurrency(summary.total_tax_amount) }}</div>
                    </div>
                    <div class="summary-stat">
                        <div class="stat-label">Collected</div>
                        <div class="stat-value has-text-success">{{ formatCurrency(summary.total_tax_paid) }}</div>
                    </div>
                    <div class="summary-stat">
                        <div class="stat-label">Owed</div>
                        <div class="stat-value has-text-danger">{{ formatCurrency(summary.total_tax_owed) }}</div>
                    </div>
                </div>

                <!-- Progress Bar -->
                <div class="mt-3">
                    <div class="progress-label mb-1">
                        <span>{{ plans.length }} of {{ summary.plan_count }} payment {{ summary.plan_count === 1 ? 'plan' : 'plans' }}</span>
                        <span class="has-text-weight-bold">{{ collectionPercent }}%</span>
                    </div>
                    <b-progress
                        :value="collectionPercent"
                        :type="collectionPercent < 50 ? 'is-danger' : collectionPercent < 80 ? 'is-warning' : 'is-success'"
                        size="is-medium">
                    </b-progress>
                </div>
            </container-list-item>

            <card-list>
                <subheader-list-item
                    title="Payment Plans with Unpaid Tax"
                    icon="fa-file-invoice-dollar"
                    :badge="plans.length"/>

                <div v-if="loading" class="has-text-centered py-5">
                    <b-loading :is-full-page="false" v-model="loading" :can-cancel="false"></b-loading>
                </div>

                <div v-else-if="plans.length === 0" class="has-text-centered py-5 has-text-grey-light">
                    <i class="fas fa-check-circle fa-3x mb-3"></i>
                    <p>No payment plans with unpaid sales tax</p>
                </div>

                <tax-plan-tile
                    v-for="plan in plans"
                    :key="plan.id"
                    :plan="plan"
                    :card="card"
                    :active="child && child.definition.component === 'payment-plan' && parseInt(child.props.planId) === plan.id"
                    @click="$openCard('payment-plan', {planId: plan.id}, card)"/>
            </card-list>
        </card-body>

    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import CardFooter from "@/TIER/components/CardFooter";
    import TaxPlanTile from "../../components/tiles/TaxPlanTile";

    import {client as http} from '@/http_client';
    import numeral from 'numeral';

    export default {
        components: {
            CardHeader,
            CardBody,
            CardToolbar,
            CardList,
            SubheaderListItem,
            ContainerListItem,
            CardFooter,
            TaxPlanTile
        },
        props: ['card', 'child'],
        data() {
            return {
                plans: [],
                summary: null,
                loading: true,
                exporting: false
            }
        },
        computed: {
            collectionPercent() {
                if (!this.summary || this.summary.total_tax_amount === 0) {
                    return 0;
                }
                return Math.round((this.summary.total_tax_paid / this.summary.total_tax_amount) * 100);
            }
        },
        methods: {
            loadData() {
                this.$emit('loading', true);
                this.loading = true;

                // Load both summary and plans in parallel
                Promise.all([
                    http.get('sales-tax/summary'),
                    http.get('sales-tax/unpaid-plans')
                ]).then(([summaryResponse, plansResponse]) => {
                    this.summary = summaryResponse.data;
                    this.plans = plansResponse.data;
                    this.loading = false;
                    this.$emit('loading', false);
                }).catch(err => {
                    console.error('Error loading tax data:', err);
                    this.$buefy.toast.open({
                        message: 'Failed to load tax data',
                        type: 'is-danger'
                    });
                    this.loading = false;
                    this.$emit('loading', false);
                });
            },
            reload() {
                this.loadData();
            },
            formatCurrency(value) {
                return numeral(value).format('$0,0.00');
            },
            exportCsv() {
                this.$emit('loading', true);
                this.exporting = true;

                http.get('sales-tax/export-csv', {
                    responseType: 'blob'
                }).then(response => {
                    // Create a download link
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;

                    // Generate filename with current date
                    const date = new Date().toISOString().split('T')[0];
                    link.setAttribute('download', `payment-plans-tax-report-${date}.csv`);

                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    this.$emit('loading', false);
                    this.exporting = false;
                }).catch(err => {
                    console.error('Error exporting CSV:', err);
                    this.$emit('loading', false);
                    this.exporting = false;
                });
            }
        },
        mounted() {
            this.loadData();
        }
    }
</script>

<style lang="scss" scoped>
    .summary-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1rem;
        margin-bottom: 1rem;

        .summary-stat {
            text-align: center;

            .stat-label {
                font-size: 0.85rem;
                color: #7a7a7a;
                margin-bottom: 0.5rem;
            }

            .stat-value {
                font-size: 1.3rem;
                font-weight: bold;
            }
        }
    }

    .progress-label {
        display: flex;
        justify-content: space-between;
        font-size: 0.9rem;
    }

    .plan-count-label {
        font-size: 0.75rem;
        color: #7a7a7a;
        text-align: center;
    }
</style>
